/**
 * The register's deadline window: two days behind one toolbar pill.
 *
 * ── WHY THIS IS NOT `FilterMenu` OR `PickerField` ──────────────────────────
 *
 * Both of those take a flat list of `{ value, label }` and hand one (or several)
 * back. A range is not a value in a list: it is two open-ended edges, either of
 * which may be empty, and the thing the operator picks is a DAY that neither
 * control can offer without enumerating a calendar. So this is a composition
 * over the same `Pop` primitive `FilterMenu` itself is built on, wearing the
 * same trigger and the same panel vocabulary - not a sixth kind of dropdown for
 * the CRM to keep in its head.
 *
 * The alternative was a row of named windows ("overdue", "next 30 days") which
 * WOULD have fitted `MenuField`. It loses to the shipped copy: the two labels in
 * `messages/*.json` are "Deadline from" and "Deadline to", which is a real range
 * that a saved view can carry and that means the same thing when the link is
 * opened in March. A named window silently means a different set every day.
 *
 * The badge counts one, not two. Both edges together are ONE narrowing - "due in
 * March" - and a pill reading 2 would say the register is under two filters.
 */
import { CalendarClock, ChevronDown, X } from "lucide-react";
import { DatePicker, cn } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { Pop } from "../../shell/Pop";

/**
 * The day as a `Date`, for bounding the other edge. Midnight local, because
 * that is the hour the calendar compares its own days at - any other and the
 * boundary day itself would be greyed out. Same rule as the logs filter's
 * `boundDate`.
 */
function edgeDate(iso: string): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

export function DeadlineFilter({
  from,
  to,
  onChange,
}: {
  /** `YYYY-MM-DD`, or "" for an open edge. */
  from: string;
  to: string;
  /** Both edges at once, so a change to one cannot be written without the other
   *  being considered - and so the caller's page reset travels in one write. */
  onChange: (next: { deadlineFrom: string; deadlineTo: string }) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const on = !!from || !!to;

  return (
    <Pop
      trigger={(open) => (
        <button
          type="button"
          // `crm-fld` is 280px wide by default, which is right for a search box
          // and wrong for a pill among pills. Utilities outrank the component
          // layer, so the width comes off here - the same way `FilterMenu` does
          // it.
          className={cn("crm-fld crm-fld-sel w-auto min-w-0 gap-2.5 px-4 text-[12.75px]", (open || on) && "is-on")}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <CalendarClock className="size-4" />
          <span>{t("projects.filterDeadline")}</span>
          {on ? <span className="crm-fld-n">1</span> : null}
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </button>
      )}
    >
      <Edge
        label={t("projects.filterDeadlineFrom")}
        value={from}
        onValue={(next) => onChange({ deadlineFrom: next, deadlineTo: to })}
        locale={locale}
        toDate={edgeDate(to)}
      />
      <Edge
        label={t("projects.filterDeadlineTo")}
        value={to}
        onValue={(next) => onChange({ deadlineFrom: from, deadlineTo: next })}
        locale={locale}
        fromDate={edgeDate(from)}
      />

      {on ? (
        <>
          <div className="crm-menu-sep" />
          {/* Clears THIS window and nothing else, hence `common.clear` rather
              than the toolbar's "clear the filters" - a row inside one control
              that emptied five others would be a trap. */}
          <button
            type="button"
            className="crm-mi"
            onClick={() => onChange({ deadlineFrom: "", deadlineTo: "" })}
          >
            <X className="size-4" />
            {t("common.clear")}
          </button>
        </>
      ) : null}
    </Pop>
  );
}

/**
 * One edge of the window: a heading and a day.
 *
 * The shared calendar, not `<input type="date">`. The old note here argued that
 * the browser draws a date field better than we would and is the only control
 * that speaks the operator's locale for free; both halves stopped being true
 * once `DatePicker` shipped, and what the native field actually gave this menu
 * was a different widget per browser and a system popup drawn over the CRM's
 * own panel. It also cost a `FIELD_KEYS` set, whose whole job was to keep
 * `Pop`'s arrow-key row walking away from the native field's day/month/year
 * segments - a button trigger has no segments, so that guard left with it.
 *
 * `crm-mdate` is the menu dress: the 34px sunken pill the panel's other field
 * wears. `index.css` explains why the size cannot be passed as utilities.
 */
function Edge({
  label,
  value,
  onValue,
  locale,
  fromDate,
  toDate,
}: {
  label: string;
  value: string;
  onValue: (v: string) => void;
  locale: string;
  /** The other edge, so the window cannot be picked backwards. */
  fromDate?: Date;
  toDate?: Date;
}) {
  return (
    <>
      <div className="crm-msect">{label}</div>
      <DatePicker
        value={value}
        onChange={onValue}
        locale={locale}
        placeholder={label}
        ariaLabel={label}
        fromDate={fromDate}
        toDate={toDate}
        className="crm-mdate"
      />
    </>
  );
}
